import { type ComponentType, lazy, type ReactNode } from 'react';
import { BiCalendarEvent, BiChat, BiErrorCircle, BiUserPlus } from 'react-icons/bi';
import { BsGlobeAmericas } from 'react-icons/bs';
import { FaRegHandshake } from 'react-icons/fa6';
import { FiTool, FiUser } from 'react-icons/fi';
import { GiCruiser } from 'react-icons/gi';
import { GoClock } from 'react-icons/go';
import { HiOutlineUserGroup } from 'react-icons/hi';
import { HiMiniWrenchScrewdriver } from 'react-icons/hi2';
import { IoMdCheckboxOutline } from 'react-icons/io';
import { IoBriefcaseOutline, IoDocumentTextOutline, IoImageOutline } from 'react-icons/io5';
import { LuListCheck } from 'react-icons/lu';
import { MdFolderOpen, MdFormatListBulleted, MdOutlineHomeRepairService } from 'react-icons/md';
import { PiCurrencyDollarBold, PiSuitcaseRollingDuotone } from 'react-icons/pi';
import { RxDashboard } from 'react-icons/rx';
import { TbChartPie, TbMapPin2, TbSpeedboat } from 'react-icons/tb';

import { ProtectedRoute } from '@/components';
import { type Permission, PERMISSIONS } from '@/permissions/permissions.catalog';
import { MANAGE_RIGHTS } from '@/permissions/route-rights';
import { UserRole } from '@/utils/roles';

/* Pages behind `lazy()`: this module is imported by the Drawer and GlobalSearch,
   so a static import here would pull every screen back into the app shell and
   undo the router's split. Direct paths, never the `@/pages` barrel. */
const AccountingPage = lazy(() => import('@/pages/AccountingPage/AccountingPage'));
const BudgetDetail = lazy(() => import('@/pages/BudgetDetail/BudgetDetail'));
const ChatPage = lazy(() => import('@/pages/ChatPage/ChatPage'));
const Crew = lazy(() => import('@/pages/Crew/Crew'));
const CrewManagement = lazy(() => import('@/pages/Crew/CrewManagement'));
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'));
const Discover = lazy(() => import('@/pages/Discover/Discover'));
const DockageCatalogue = lazy(
  () => import('@/pages/DockagePage/DockageCatalogue/DockageCatalogue'),
);
const DocumentsPage = lazy(() => import('@/pages/Documents/DocumentsPage'));
const EventsPage = lazy(() => import('@/pages/EventsPage/EventsPage'));
const Maintain = lazy(() => import('@/pages/Maintain/Maintain'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage/MaintenancePage'));
const Manage = lazy(() => import('@/pages/Manage/Manage'));
const Plan = lazy(() => import('@/pages/Plan/Plan'));
const SafetyChecklist = lazy(() => import('@/pages/SafetyChecklist/SafetyChecklist'));
const ServiceHistory = lazy(() => import('@/pages/ServiceHistory/ServiceHistory'));
const ServiceProvidersPage = lazy(() => import('@/pages/ServiceProviders/ServiceProvidersPage'));
const Spending = lazy(() => import('@pages/Expenses/Spending/Spending'));
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage/SubscriptionPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage/TasksPage'));
const Voyages = lazy(() => import('@/pages/Voyages/Voyages'));
const GuestsPage = lazy(() => import('@/pages/GuestProfile/GuestsPage'));
const DailyLog = lazy(() => import('@/pages/LogbookPage/DailyLog/DailyLog'));
const LogbookPage = lazy(() => import('@/pages/LogbookPage/LogbookPage'));
const NavigationLog = lazy(() => import('@/pages/LogbookPage/NavigationLog/NavigationLog'));
const OilLog = lazy(() => import('@/pages/LogbookPage/OilLog/OilLog'));
const SafetyLog = lazy(() => import('@/pages/LogbookPage/SafetyLog/SafetyLog'));
const TrashLog = lazy(() => import('@/pages/LogbookPage/TrashLog/TrashLog'));
const MyProfilePage = lazy(() =>
  import('@/pages/MyProfilePage/MyProfilePage').then((mod) => ({ default: mod.MyProfilePage })),
);

export type ChildRoute = {
  path: string;
  component: ComponentType;
  permission?: Permission;
  anyOf?: Permission[];
  allOf?: Permission[];
  /** Names the child in a plan-locked placeholder; falls back to the parent's label. */
  label?: string;
};

export type AppRouteConfig = {
  key: string;
  path: string;
  label: string;
  icon: ReactNode;
  component: ComponentType;
  permission?: Permission;
  anyOf?: Permission[];
  allOf?: Permission[];
  /** Redirect to /dashboard if the user's primary role matches this value. */
  denyRole?: UserRole;
  mobileOnly?: boolean;
  /** Nav item only — route object is defined separately in router.tsx (e.g. has children) */
  navOnly?: boolean;
  /** React Router nested children; parent component must render <Outlet /> */
  children?: ChildRoute[];
};

export type NavItem = {
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
  permission?: Permission;
  anyOf?: Permission[];
  mobileOnly?: boolean;
  /**
   * The vessel's plan does not carry this destination, but a bigger one would. Kept in the
   * nav rather than dropped, marked with a padlock, and opening it lands on the placeholder
   * the route guard renders. Only ever set for the owner — see `AccessVerdict`.
   */
  locked?: boolean;
};

export const REGULAR_NAV_ROUTES: AppRouteConfig[] = [
  {
    key: 'plan',
    label: 'Plan',
    icon: <BiCalendarEvent />,
    path: 'plan',
    component: Plan,
  },
  {
    key: 'manage',
    label: 'Manage',
    icon: <IoBriefcaseOutline />,
    path: 'manage',
    component: Manage,
    // The guard on the hub's own route in `router.tsx`: the destination is a wall on a plan
    // carrying none of these, so the nav has to know that too — dropped for crew, padlocked
    // for the owner.
    anyOf: [...MANAGE_RIGHTS],
  },
  {
    key: 'maintain',
    label: 'Maintain',
    icon: <FiTool />,
    path: 'maintain',
    component: Maintain,
    anyOf: [
      PERMISSIONS.activity.ExternalTasks,
      PERMISSIONS.activity.Tasks,
      PERMISSIONS.vessel.Equipment,
    ],
  },
  {
    key: 'discover',
    label: 'Discover',
    icon: <BsGlobeAmericas />,
    path: 'discover',
    component: Discover,
    navOnly: true,
  },
  {
    key: 'my-yachts',
    path: 'my-yachts',
    label: 'My Vessels',
    icon: <TbSpeedboat />,
    component: Dashboard,
    navOnly: true,
  },
  {
    key: 'dashboard',
    path: 'dashboard',
    label: 'Dashboard',
    icon: <RxDashboard fill="red" stroke="red" />,
    component: Dashboard,
  },
  {
    key: 'events',
    path: 'events',
    label: 'Events',
    icon: <GoClock />,
    component: EventsPage,
    permission: PERMISSIONS.activity.GetEventsInfo,
  },
  {
    key: 'service-providers',
    path: 'service-providers',
    label: 'Service Providers',
    icon: <FaRegHandshake />,
    component: ServiceProvidersPage,
    // Mirrors service-provider's `basicServiceProvider` middleware — the catalogue opens on
    // either right, and navigate carries the internal one without the basic one.
    anyOf: [
      PERMISSIONS['service-provider'].BasicServiceProvider,
      PERMISSIONS['service-provider'].ServiceProviderInternal,
    ],
  },
  {
    key: 'dockage-catalogue',
    path: 'dockage-catalogue',
    label: 'Marina Services',
    icon: <TbMapPin2 />,
    component: DockageCatalogue,
    // Mirrors service-provider's `basicServiceProvider` middleware — the catalogue opens on
    // either right, and navigate carries the internal one without the basic one.
    anyOf: [
      PERMISSIONS['service-provider'].BasicServiceProvider,
      PERMISSIONS['service-provider'].ServiceProviderInternal,
    ],
  },
  {
    key: 'inbox',
    path: 'inbox',
    label: 'Chat',
    icon: <BiChat />,
    component: ChatPage,
    // permission: PERMISSIONS.communication.RoomsBasic,
  },
  {
    key: 'spending',
    path: 'spending',
    label: 'Expenses',
    icon: <PiCurrencyDollarBold />,
    component: Spending,
    anyOf: [
      PERMISSIONS.wallet.TransactionsBasic,
      PERMISSIONS.wallet.Transactions,
      PERMISSIONS.wallet.Quotes,
      PERMISSIONS.wallet.Invoices,
    ],
  },
  {
    key: 'maintenance',
    path: 'maintenance',
    label: 'Maintenance',
    icon: <IoMdCheckboxOutline />,
    component: MaintenancePage,
    permission: PERMISSIONS.activity.Maintenance,
  },
  {
    key: 'tasks',
    path: 'tasks',
    label: 'Tasks',
    icon: <MdFormatListBulleted />,
    component: TasksPage,
    permission: PERMISSIONS.activity.Maintenance,
  },
  {
    key: 'service-history',
    path: 'service-history',
    label: 'Service History',
    icon: <HiMiniWrenchScrewdriver />,
    component: ServiceHistory,
    permission: PERMISSIONS.activity.Maintenance,
    navOnly: true,
  },
  {
    key: 'documents',
    path: 'documents/*',
    label: 'Library',
    icon: <IoDocumentTextOutline />,
    component: DocumentsPage,
    permission: PERMISSIONS.document.DocumentBasic,
  },
  {
    key: 'crew',
    path: 'crew',
    label: 'Hire Crew',
    icon: <BiUserPlus />,
    component: Crew,
  },
  {
    key: 'crew-management',
    path: 'crew-management',
    label: 'Crew Management',
    icon: <HiOutlineUserGroup />,
    component: CrewManagement,
    permission: PERMISSIONS.users.CrewManagement,
  },
  {
    key: 'guests',
    path: 'guests',
    label: 'Guests',
    icon: <PiSuitcaseRollingDuotone />,
    component: GuestsPage,
  },
  {
    key: 'voyages',
    path: 'voyages',
    label: 'Voyages',
    icon: <GiCruiser />,
    component: Voyages,
    permission: PERMISSIONS.activity.GetEventsInfo,
    denyRole: UserRole.GUEST,
  },
  {
    key: 'my-profile',
    path: 'my-profile',
    label: 'My Profile',
    icon: <FiUser />,
    component: MyProfilePage,
    children: [{ path: 'subscription', component: SubscriptionPage }],
  },
  {
    key: 'logbook',
    path: 'logbook',
    label: 'Logbook',
    icon: <MdFormatListBulleted />,
    component: LogbookPage,
    anyOf: [
      PERMISSIONS.logbook.DailyLog,
      PERMISSIONS.logbook.DigitalNavigationLog,
      PERMISSIONS.logbook.TrashLog,
      PERMISSIONS.logbook.SafetyLog,
      PERMISSIONS.logbook.OilLog,
    ],
    children: [
      {
        path: 'vessel-daily-log',
        label: 'Daily Log',
        component: DailyLog,
        permission: PERMISSIONS.logbook.DailyLog,
      },
      {
        path: 'daily-navigation-log',
        label: 'Daily Navigation Log',
        component: NavigationLog,
        permission: PERMISSIONS.logbook.DigitalNavigationLog,
      },
      {
        path: 'trash-log',
        label: 'Trash Log',
        component: TrashLog,
        permission: PERMISSIONS.logbook.TrashLog,
      },
      {
        path: 'safety-log',
        label: 'Safety Log',
        component: SafetyLog,
        permission: PERMISSIONS.logbook.SafetyLog,
      },
      {
        path: 'oil-log',
        label: 'Oil Log',
        component: OilLog,
        permission: PERMISSIONS.logbook.OilLog,
      },
    ],
  },
  {
    key: 'safety-checklist',
    path: 'safety-checklist',
    label: 'Emergency',
    icon: <BiErrorCircle />,
    component: SafetyChecklist,
  },
  {
    // The page is mounted under the Manage layout in router.tsx, so this entry
    // only supplies the nav/search link — registering it here as well would put
    // a second, layout-less copy of the page at a top-level URL.
    key: 'budget-details',
    path: 'manage/budget-details',
    label: 'Budget Details',
    icon: <TbChartPie />,
    component: BudgetDetail,
    permission: PERMISSIONS.wallet.GetBudgets,
    navOnly: true,
  },
];

export const SP_NAV_ROUTES: AppRouteConfig[] = [
  {
    key: 'my-service',
    path: 'my-services',
    label: 'My services',
    icon: <MdOutlineHomeRepairService />,
    component: Dashboard,
    navOnly: true,
  },
  {
    key: 'dashboard',
    path: 'dashboard',
    label: 'Dashboard',
    icon: <RxDashboard fill="red" stroke="red" />,
    component: Dashboard,
  },
  {
    key: 'maintenance',
    path: 'maintenance',
    label: 'Job',
    icon: <IoMdCheckboxOutline />,
    component: MaintenancePage,
  },
  {
    key: 'inbox',
    path: 'inbox',
    label: 'Chat',
    icon: <BiChat />,
    component: ChatPage,
  },
  // {
  //   key: 'clients',
  //   path: 'clients',
  //   label: 'ClientsPage',
  //   icon: <GoPeople />,
  //   component: ClientsPage,
  //   permission: PERMISSIONS['service-provider'].BasicServiceProvider,
  // },
  {
    key: 'accounting',
    path: 'accounting',
    label: 'Accounting',
    icon: <PiCurrencyDollarBold />,
    component: AccountingPage,
    permission: PERMISSIONS['service-provider'].ServiceProviderInternal,
  },
  {
    key: 'service-providers',
    path: 'service-providers',
    label: 'Service Provider Catalog',
    icon: <FaRegHandshake />,
    component: ServiceProvidersPage,
    // Mirrors service-provider's `basicServiceProvider` middleware — the catalogue opens on
    // either right, and navigate carries the internal one without the basic one.
    anyOf: [
      PERMISSIONS['service-provider'].BasicServiceProvider,
      PERMISSIONS['service-provider'].ServiceProviderInternal,
    ],
  },
  {
    key: 'dockage-catalogue',
    path: 'dockage-catalogue',
    label: 'Marina services',
    icon: <TbMapPin2 />,
    component: DockageCatalogue,
    // Mirrors service-provider's `basicServiceProvider` middleware — the catalogue opens on
    // either right, and navigate carries the internal one without the basic one.
    anyOf: [
      PERMISSIONS['service-provider'].BasicServiceProvider,
      PERMISSIONS['service-provider'].ServiceProviderInternal,
    ],
  },
  {
    key: 'my-profile',
    path: 'my-profile',
    label: 'My Profile',
    icon: <FiUser />,
    component: MyProfilePage,
    children: [{ path: 'subscription', component: SubscriptionPage }],
  },
];

export function toNavItem(route: AppRouteConfig): NavItem {
  return {
    key: route.key,
    to: `/${route.path.replace('/*', '')}`,
    label: route.label,
    icon: route.icon,
    permission: route.permission,
    anyOf: route.anyOf,
    mobileOnly: route.mobileOnly,
  };
}

/** A nav group after permission/mobile filtering, ready to render. */
export type NavGroupView = {
  key: string;
  label: string;
  icon: ReactNode;
  standalone?: boolean;
  startsSection?: boolean;
  items: NavItem[];
};

/**
 * Single source of truth for nav grouping. Groups reference existing routes by
 * key (the flat arrays above stay the source for the router). `label` overrides
 * the sub-item label to match the design copy; `icon` is the category icon.
 */
type NavGroupDef = {
  key: string;
  label: string;
  icon: ReactNode;
  /** Render as one direct link with no accordion (e.g. Dashboard, My Profile). */
  standalone?: boolean;
  startsSection?: boolean;
  items: { key: string; label?: string }[];
};

const REGULAR_NAV_GROUP_DEFS: NavGroupDef[] = [
  // {
  //   key: 'my-yachts',
  //   label: 'My yachts',
  //   icon: <TbSpeedboat />,
  //   standalone: true,
  //   items: [{ key: 'my-yachts' }],
  // },
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <RxDashboard fill="red" stroke="red" />,
    standalone: true,
    items: [{ key: 'dashboard' }],
  },
  {
    key: 'plan',
    label: 'Plan',
    icon: <BiCalendarEvent />,
    standalone: true,
    items: [{ key: 'plan' }],
  },
  {
    key: 'manage',
    label: 'Manage',
    icon: <IoBriefcaseOutline />,
    standalone: true,
    items: [
      { key: 'manage' },
      { key: 'invoices', label: 'Invoices' },
      { key: 'transactions', label: 'Expenses' },
      { key: 'budget-details', label: 'Budget Details' },
    ],
  },
  {
    key: 'maintain',
    label: 'Maintain',
    icon: <FiTool />,
    standalone: true,
    items: [{ key: 'maintain' }],
  },
  {
    key: 'discover',
    label: 'Discover',
    icon: <BsGlobeAmericas />,
    standalone: true,
    items: [{ key: 'discover' }],
  },
  {
    key: 'inbox',
    label: 'Messages',
    icon: <BiChat />,
    standalone: true,
    startsSection: true,
    items: [{ key: 'inbox' }],
  },
  {
    key: 'tasks',
    label: 'Tasks',
    icon: <LuListCheck />,
    standalone: true,
    items: [{ key: 'tasks' }],
  },
  // {
  //   key: 'workspace',
  //   label: 'Workspace',
  //   icon: <TbDeviceDesktopAnalytics />,
  //   items: [
  //     { key: 'inbox', label: 'Chat' },
  //     { key: 'events', label: 'All Events' },
  //     { key: 'tasks', label: 'All Tasks' },
  //     { key: 'voyages', label: 'Voyages' },
  //   ],
  // },
  // {
  //   key: 'maintenance',
  //   label: 'Maintenance',
  //   icon: <IoMdCheckboxOutline />,
  //   items: [
  //     { key: 'maintenance', label: 'Maintenance tasks' },
  //     { key: 'service-history', label: 'Service History' },
  //     { key: 'equipment/categories', label: 'Equipment' },
  //   ],
  // },
  // {
  //   key: 'financial',
  //   label: 'Financial',
  //   icon: <PiCurrencyDollarBold />,
  //   items: [
  //     { key: 'spending', label: 'Expenses' },
  //     { key: 'budget-details', label: 'Budget Details' },
  //   ],
  // },
  // {
  //   key: 'crew',
  //   label: 'Crew',
  //   icon: <HiOutlineUserGroup />,
  //   items: [
  //     { key: 'crew', label: 'Hire crew' },
  //     { key: 'crew-management', label: 'Crew Management' },
  //   ],
  // },
  // {
  //   key: 'services',
  //   label: 'Services',
  //   icon: <FaRegHandshake />,
  //   items: [
  //     { key: 'service-providers', label: 'Find Service provider' },
  //     { key: 'dockage-catalogue', label: 'Book Marina' },
  //   ],
  // },
  {
    key: 'documents',
    label: 'Library',
    icon: <MdFolderOpen />,
    standalone: true,
    items: [{ key: 'documents' }],
  },
  // {
  //   key: 'my-profile',
  //   label: 'My Profile',
  //   icon: <FiUser />,
  //   standalone: true,
  //   items: [{ key: 'my-profile' }],
  // },
];

const SP_NAV_GROUP_DEFS: NavGroupDef[] = [
  {
    key: 'my-service',
    label: 'My Services',
    icon: <MdOutlineHomeRepairService />,
    standalone: true,
    items: [{ key: 'my-service' }],
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <RxDashboard fill="red" stroke="red" />,
    standalone: true,
    items: [{ key: 'dashboard' }],
  },
  {
    key: 'inbox',
    label: 'Messages',
    icon: <BiChat />,
    standalone: true,
    startsSection: true,
    items: [{ key: 'inbox' }],
  },
  {
    key: 'jobs',
    label: 'Jobs',
    icon: <IoMdCheckboxOutline />,
    items: [{ key: 'maintenance', label: 'Job' }],
  },
  {
    key: 'financial',
    label: 'Financial',
    icon: <PiCurrencyDollarBold />,
    items: [{ key: 'accounting', label: 'Accounting' }],
  },
  {
    key: 'services',
    label: 'Services',
    icon: <FaRegHandshake />,
    items: [
      { key: 'service-providers', label: 'Service Provider Catalog' },
      { key: 'dockage-catalogue', label: 'Marina Services' },
    ],
  },
  // {
  //   key: 'my-profile',
  //   label: 'My Profile',
  //   icon: <FiUser />,
  //   standalone: true,
  //   items: [{ key: 'my-profile' }],
  // },
];

// Nav groups hidden entirely for the GUEST primary role (role-based, not permission-based —
// see PR #100 for why permission gates alone are insufficient for guest suppression).
const GUEST_HIDDEN_GROUP_KEYS = new Set(['maintenance', 'crew']);
// Individual items to hide for guests within groups that are otherwise kept.
const GUEST_HIDDEN_ITEM_KEYS: Record<string, Set<string>> = {
  workspace: new Set(['voyages']),
};

/**
 * Returns the nav groups visible to the current user, with each group's routes
 * resolved to `NavItem`s and filtered by mobile/permission. SP users bypass
 * permission filtering (mirroring the router); empty groups are dropped.
 */
export function getVisibleNavGroups(opts: {
  isServiceProvider: boolean;
  isGuest: boolean;
  isMobile: boolean;
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  vesselId?: string | null;
  /**
   * Whether a destination the user cannot reach is one their plan withholds and an upgrade
   * would restore. Such items stay in the nav, flagged `locked`. Omit it — as the global
   * search does — and every unreachable item is dropped, which is the older behaviour.
   */
  isPlanLocked?: (permissions: Permission[]) => boolean;
}): NavGroupView[] {
  const { isServiceProvider, isGuest, isMobile, can, canAny, vesselId, isPlanLocked } = opts;
  const defs = isServiceProvider ? SP_NAV_GROUP_DEFS : REGULAR_NAV_GROUP_DEFS;
  const routes = isServiceProvider ? SP_NAV_ROUTES : REGULAR_NAV_ROUTES;
  const byKey = new Map(routes.map((r) => [r.key, r]));

  return defs.flatMap((def): NavGroupView[] => {
    if (isGuest && GUEST_HIDDEN_GROUP_KEYS.has(def.key)) return [];

    const guestHiddenItems = GUEST_HIDDEN_ITEM_KEYS[def.key];
    const items = def.items
      .map(({ key, label }) => {
        const route = byKey.get(key);
        if (!route) return null;
        const navItem = toNavItem(route);
        return label ? { ...navItem, label } : navItem;
      })
      .filter((item): item is NavItem => item !== null)
      .filter(
        (item) => !(isGuest && guestHiddenItems?.has(item.key)) && (!item.mobileOnly || isMobile),
      )
      .flatMap((item): NavItem[] => {
        // SP role bypasses permission filtering, mirroring the router.
        if (isServiceProvider) return [item];

        const { permission, anyOf = [] } = item;
        const permissionUnmet = permission && !can(permission) ? permission : null;
        const anyOfUnmet = anyOf.length > 0 && !canAny(anyOf);
        if (!permissionUnmet && !anyOfUnmet) return [item];

        if (!isPlanLocked) return [];

        /**
         * Every unmet clause has to be one an upgrade settles, or the padlock would promise
         * access that paying more will not deliver. `anyOf` needs only one of its
         * permissions back to open, so one plan-locked member is enough.
         */
        const upgradeOpensIt =
          (!permissionUnmet || isPlanLocked([permissionUnmet])) &&
          (!anyOfUnmet || anyOf.some((name) => isPlanLocked([name])));

        return upgradeOpensIt ? [{ ...item, locked: true }] : [];
      });

    // Memories is the vessel's gallery — Library's second screen, not a folder:
    // the old `<vesselId>.gallery` document folder was deleted by the backend.
    if (def.key === 'documents' && vesselId) {
      items.push({
        key: 'documents/memories',
        to: '/documents/memories',
        label: 'Memories',
        icon: <IoImageOutline />,
      });
    }

    if (items.length === 0) return [];
    return [
      {
        key: def.key,
        label: def.label,
        icon: def.icon,
        standalone: def.standalone,
        startsSection: def.startsSection,
        items,
      },
    ];
  });
}

function makeRouteElement(route: AppRouteConfig, skipPermissionsForSp = false) {
  const Component = route.component;
  return (
    <ProtectedRoute
      requirePermission={route.permission}
      requireAnyPermission={route.anyOf}
      requireAllPermissions={route.allOf}
      denyRole={route.denyRole}
      skipPermissionsIfAnyRole={skipPermissionsForSp ? [UserRole.SERVICE_PROVIDER] : undefined}
      lockTitle={route.label}
    >
      <Component />
    </ProtectedRoute>
  );
}

function makeChildElement(child: ChildRoute, parent: AppRouteConfig) {
  const Component = child.component;
  return (
    <ProtectedRoute
      requirePermission={child.permission ?? parent.permission}
      requireAnyPermission={child.anyOf ?? parent.anyOf}
      requireAllPermissions={child.allOf ?? parent.allOf}
      lockTitle={child.label ?? parent.label}
    >
      <Component />
    </ProtectedRoute>
  );
}

/**
 * Returns deduplicated React Router route objects for all navigable routes.
 * Routes shared between regular users and SP users get no permission guard —
 * the SP role bypasses permission filtering that only applies to regular users.
 * Routes exclusive to one user type keep their permission guard.
 */
export function getRouterRoutes() {
  const spPaths = new Set(SP_NAV_ROUTES.map((r) => r.path));
  const seen = new Set<string>();
  const result: {
    path: string;
    element: ReactNode;
    children?: { path: string; element: ReactNode }[];
  }[] = [];

  for (const route of REGULAR_NAV_ROUTES) {
    if (!seen.has(route.path) && !route.navOnly) {
      seen.add(route.path);
      const routeObj: (typeof result)[number] = {
        path: route.path,
        element: makeRouteElement(route, spPaths.has(route.path)),
      };
      if (route.children?.length) {
        routeObj.children = route.children.map((child) => ({
          path: child.path,
          element: makeChildElement(child, route),
        }));
      }
      result.push(routeObj);
    }
  }

  for (const route of SP_NAV_ROUTES) {
    if (!seen.has(route.path) && !route.navOnly) {
      seen.add(route.path);
      result.push({ path: route.path, element: makeRouteElement(route) });
    }
  }

  return result;
}
